import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, ColumnPanel, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { NumberWithUnitsFormatter } from '@ddt-web/data-service';
import { IretireUserDataState } from '@ddt-web/shared-stores';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { HeadingComponent, HeadingLevel } from '../heading/heading-component';
import { HighchartsSVGComponent } from '../highchart/highchart-svg-component';
import { PROGRESS_BAR_COLOR_CONFIG } from './chart-colors.config';

export class EstimatedRetirementIncomeComponent extends BusinessComponent {
	public static readonly FIXED_INDEX = 'FIXED_INDEX';
	public incomeBarData: { [id: string]: number } = {};
	public formatter = new NumberWithUnitsFormatter('0', '-', true);
	public lineWidth: number;
	public lowerBoundIncomeRangeLimit: number;
	public upperBoundIncomeRangeLimit: number;
	public grossLowerBoundIncomeRangeLimit: number;
	public grossUpperBoundIncomeRangeLimit: number;

	constructor(
		public name: string,
		public isAnnuityVariant: boolean,
		public incomeBarValues$: Observable<any>,
		public contentService: ContentService,
		public displayCurrency: string,
		public userData?: IretireUserDataState,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible }));
		this.incomeBarValues$
			.pipe(
				first(data => !!data),
				map(data => {
					let incomeValues = data['netIncomeProjection'] ?? data['incomeProjection'];
					if (data['netIncomeProjection'] && data['incomeProjection']) {
						//  check if both income and net income objects present
						incomeValues = {
							...incomeValues,
							grossLowerBoundIncome: data['incomeProjection']?.lowerBoundIncome,
							grossUpperBoundIncome: data['incomeProjection']?.upperBoundIncome,
							grossExpectedIncome: data['incomeProjection']?.expectedIncome,
							lowerBoundIncome1Year: data['netIncomeProjection']?.lowerBoundIncome1Year,
							upperBoundIncome1Year: data['netIncomeProjection']?.upperBoundIncome1Year,
							grossLowerBoundIncome1Year: data['incomeProjection']?.lowerBoundIncome1Year,
							grossUpperBoundIncome1Year: data['incomeProjection']?.upperBoundIncome1Year
						};
					}
					return incomeValues;
				})
			)
			.subscribe(incomeData => {
				this.incomeBarData = incomeData;
				this.lineWidth = (0.3 * Number(this.incomeBarData['desiredIncome'])) / 100;
				if (this.userData?.checkinFrequency === 'annual') {
					this.lowerBoundIncomeRangeLimit = this.incomeBarData.lowerBoundIncome1Year;
					this.upperBoundIncomeRangeLimit = this.incomeBarData.upperBoundIncome1Year;
					this.grossLowerBoundIncomeRangeLimit = this.incomeBarData.grossLowerBoundIncome1Year;
					this.grossUpperBoundIncomeRangeLimit = this.incomeBarData.grossUpperBoundIncome1Year;
				} else {
					this.lowerBoundIncomeRangeLimit = this.incomeBarData?.lowerBoundIncome;
					this.upperBoundIncomeRangeLimit = this.incomeBarData?.upperBoundIncome;
					this.grossLowerBoundIncomeRangeLimit = this.incomeBarData.grossLowerBoundIncome;
					this.grossUpperBoundIncomeRangeLimit = this.incomeBarData.grossUpperBoundIncome;
				}
			});
	}

	public buildComponent(): AbstractRenderer<SVGGElement>[] {
		const panels = [];
		panels.push(
			...[
				new HeadingComponent('ESTIMATED_ANNUAL_RETIREMENT_INCOME_HEADER', 'Estimated Annual Retirement Income', HeadingLevel.H2),
				new StaticParagraphComponent('IRETIRE_PLAN_DETAILS', 'YOUR IRETIRE PLAN COULD GET YOU:', {}, { marginTop: 10, marginBottom: 5 }, ['text-bold']),
				this.buildIncomeBarDetails(),
				new RowPanel('Income bar', [this.isAnnuityVariant ? this.getAnnuityIncomeBar() : this.getIncomeBar()], { marginBottom: this.isAnnuityVariant ? 20 : 10 }),
				{ useFullWidth: true },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			]
		);
		return panels;
	}

	private buildIncomeBarDetails(): RowPanel {
		const grossIncomePanels: RowPanel[] = [];
		// add rows into grossIncome column only if gross income is present
		if (this.incomeBarData?.grossLowerBoundIncome) {
			grossIncomePanels.push(
				new RowPanel(
					'Estimated Gross Income',
					[
						new StaticParagraphComponent('', `${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['grossExpectedIncome']))}`, {}, {}, [
							'text-bold',
							'text-14'
						])
					],
					{ marginBottom: 0 },
					Alignment.Horizontal.Left
				),
				new RowPanel(
					'Estimated Gross Income Label',
					[
						new StaticParagraphComponent('', this.contentService.get('ESTIMATED_GROSS_INCOME_TEXT', '* estimated gross income each year in retirement'), {}, {}, [
							'text-9'
						])
					],
					{ marginBottom: 0 },
					Alignment.Horizontal.Left
				),
				new RowPanel(
					'Estimated Gross Income Range',
					[
						new StaticParagraphComponent(
							'',
							this.contentService.get('ESTIMATED_GROSS_INCOME_RANGE', '* Estimated annual gross income range:') +
							` ${this.displayCurrency}${this.formatter.format(Number(this.grossLowerBoundIncomeRangeLimit))} to ${this.displayCurrency}${this.formatter.format(
								Number(this.grossUpperBoundIncomeRangeLimit)
							)}`
						)
					],
					{ marginBottom: 5 },
					Alignment.Horizontal.Left
				)
			);
		}

		return new RowPanel(
			'iRetire Plan Specifics',
			[
				new ColumnPanel(
					'Net Income',
					[
						new RowPanel(
							'Estimated Net Income',
							[
								new StaticParagraphComponent('', `${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['expectedIncome']))}`, {}, {}, [
									'text-bold',
									'text-14'
								])
							],
							{ marginBottom: 0 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						),
						new RowPanel(
							'Estimated Net Income label',
							[
								new StaticParagraphComponent('', this.contentService.get('ESTIMATED_NET_INCOME_TEXT', '* estimated net income each year in retirement'), {}, {}, [
									'text-9'
								])
							],
							{ marginBottom: 0 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						),
						new RowPanel(
							'Estimated Net Income Range',
							[
								new StaticParagraphComponent(
									'',
									this.contentService.get('ESTIMATED_NET_INCOME_RANGE', '* Estimated annual net income range:') +
									` ${this.displayCurrency}${this.formatter.format(Number(this.lowerBoundIncomeRangeLimit))} to ${this.displayCurrency
									}${this.formatter.format(Number(this.upperBoundIncomeRangeLimit))}`
								)
							],
							{ marginBottom: 5 },
							Alignment.Horizontal.Left,
							Alignment.Vertical.Top
						)
					],
					{ proportion: 50, paddingRight: 20 },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Top
				),
				new ColumnPanel(
					'Gross Income',
					grossIncomePanels, // empty column panel if gross income not present
					{ proportion: 50, paddingLeft: 20 },
					Alignment.Horizontal.Left
				)
			],
			{ marginBottom: 5 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Top
		);
	}

	private getIncomeBar(): HighchartsSVGComponent {
		let chartData: any;
		if (Number(this.incomeBarData['desiredIncome']) > Number(this.incomeBarData['expectedIncome'])) {
			chartData = this.getSeriesData();
		} else {
			chartData = this.getSeriesDataWhenGoalIsAchieved();
		}

		return new HighchartsSVGComponent('IncomeBar', { height: 100 }, chartData, 'progressBar', {
			growthMultiplier: 10000,
			displayCurrency: this.displayCurrency,
			customData: {
				header: '',
				subHeader: ''
			}
		});
	}

	private getSeriesData(): any {
		let lowerParam: number;
		let upperParam: number;
		let lowerParamLegend: string;
		let upperParamLegend: string;
		let lowerParamColor: string;
		let upperParamLColor: string;

		if (Number(this.incomeBarData['expectedIncome']) > Number(this.incomeBarData['currentIncome'])) {
			lowerParam = Number(this.incomeBarData['currentIncome']);
			upperParam = Number(this.incomeBarData['expectedIncome']);
			lowerParamLegend = this.contentService.get('CURRENT_STANDING', ' Current Standing');
			upperParamLegend = this.contentService.get('WITH_IRETIRE_PLAN', ' with your iRetire Plan*');
			lowerParamColor = PROGRESS_BAR_COLOR_CONFIG.CURRENT_STANDING;
			upperParamLColor = PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN;
		} else {
			upperParam = Number(this.incomeBarData['currentIncome']);
			lowerParam = Number(this.incomeBarData['expectedIncome']);
			upperParamLegend = this.contentService.get('CURRENT_STANDING', ' Current Standing');
			lowerParamLegend = this.contentService.get('WITH_IRETIRE_PLAN', ' with your iRetire Plan*');
			lowerParamColor = PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN;
			upperParamLColor = PROGRESS_BAR_COLOR_CONFIG.CURRENT_STANDING;
		}
		return {
			series: [
				{
					name: '',
					data: [(3 * Number(this.incomeBarData['desiredIncome'])) / 100],
					color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
					grouping: false,
					showInLegend: false
				},
				{
					name:
						`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['desiredIncome']))}` +
						this.contentService.get('YOUR_INCOME_GOAL', ' Your Income Goal'),
					data: [this.lineWidth],
					color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GOAL,
					grouping: false
				},
				{
					name:
						`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['desiredIncome']) - this.incomeBarData['expectedIncome'])}` +
						this.contentService.get('YOUR_INCOME_GAP', ' Your Income Gap'),
					data: [Number(this.incomeBarData['desiredIncome']) - upperParam],
					color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
					grouping: false
				},
				{
					name: `${this.displayCurrency}${this.formatter.format(upperParam)}` + upperParamLegend,
					data: [upperParam - lowerParam],
					color: upperParamLColor,
					grouping: false
				},
				{
					name: `${this.displayCurrency}${this.formatter.format(lowerParam)}` + lowerParamLegend,
					data: [lowerParam],
					color: lowerParamColor,
					grouping: false
				}
			],
			max: Number(this.incomeBarData['desiredIncome']),
			goalAchieved: false
		};
	}

	private getSeriesDataWhenGoalIsAchieved(): any {
		this.lineWidth = (0.3 * Number(this.incomeBarData.expectedIncome)) / 100;
		return {
			series: [
				{
					name: `${this.displayCurrency}${this.formatter.format(0)}` + this.contentService.get('YOUR_INCOME_GAP', ' Your Income Gap'),
					data: [0],
					color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
					grouping: false
				},
				{
					name:
						`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['expectedIncome']))}` +
						this.contentService.get('WITH_IRETIRE_PLAN', ' with your iRetire Plan*'),
					data: [Number(this.incomeBarData['expectedIncome']) - Number(this.incomeBarData['desiredIncome'])],
					color: PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN,
					grouping: false
				},
				{
					name:
						`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['desiredIncome']))}` +
						this.contentService.get('YOUR_INCOME_GOAL', ' Your Income Goal'),
					data: [this.lineWidth],
					color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GOAL,
					grouping: false
				},
				{
					name: ``,
					data: [Number(this.incomeBarData['desiredIncome']) - Number(this.incomeBarData['currentIncome'])],
					color: PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN,
					grouping: false,
					showInLegend: false
				},
				{
					name:
						`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData['currentIncome']))}` +
						this.contentService.get('CURRENT_STANDING', ' Current Standing'),
					data: [Number(this.incomeBarData['currentIncome'])],
					color: PROGRESS_BAR_COLOR_CONFIG.CURRENT_STANDING,
					grouping: false
				}
			],
			max: Number(this.incomeBarData['expectedIncome']),
			goalAchieved: true
		};
	}

	private getAnnuityIncomeBar(): HighchartsSVGComponent {
		let chartData: any;
		if (Number(this.incomeBarData.desiredIncome) > Number(this.incomeBarData.expectedIncome)) {
			chartData = this.getAnnuitySeriesData();
		} else {
			chartData = this.getAnnuitySeriesDataWhenGoalAchieved();
		}
		return new HighchartsSVGComponent('AnnuityIncomeBar', { height: 130 }, chartData, 'progressBar', {
			growthMultiplier: 10000,
			isAnnuityVariant: true,
			displayCurrency: this.displayCurrency,
			customData: {
				header: '',
				subHeader: ''
			}
		});
	}

	private getAnnuitySeriesData(): any {
		const series = [
			{
				name: '',
				data: [(3 * Number(this.incomeBarData.desiredIncome)) / 100],
				color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
				grouping: false,
				showInLegend: false
			},
			{
				name:
					`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.desiredIncome))}` + this.contentService.get('YOUR_INCOME_GOAL', ' Your Income Goal'),
				data: [this.lineWidth],
				color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GOAL,
				grouping: false
			},
			{
				name:
					`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.desiredIncome) - Number(this.incomeBarData.expectedIncome))}` +
					this.contentService.get('PDF_ANNUITY_INCOME_GAP', ' Income Gap'),
				data: [Number(this.incomeBarData.desiredIncome) - Number(this.incomeBarData.expectedIncome)],
				color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
				grouping: false
			},
			{
				name: '',
				data: [this.lineWidth],
				color: PROGRESS_BAR_COLOR_CONFIG.ANNUITY_IRETIRE_PLAN,
				grouping: false,
				showInLegend: false
			}
		];
		series.push(...this.getRemainingSeriesData());
		return {
			series: series,
			max: Number(this.incomeBarData.desiredIncome),
			goalAchieved: false
		};
	}

	private getAnnuitySeriesDataWhenGoalAchieved(): any {
		this.lineWidth = (0.3 * Number(this.incomeBarData.expectedIncome)) / 100;
		const incomeDifference = Number(this.incomeBarData.expectedIncome) - Number(this.incomeBarData.desiredIncome);
		const series = [
			{
				name: `${this.displayCurrency}${this.formatter.format(0)}` + this.contentService.get('PDF_ANNUITY_INCOME_GAP', ' Income Gap'),
				data: [0],
				color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GAP,
				grouping: false
			},
			{
				name: '',
				data: [this.lineWidth],
				color: PROGRESS_BAR_COLOR_CONFIG.ANNUITY_IRETIRE_PLAN,
				grouping: false,
				showInLegend: false
			},
			{
				name: `${this.displayCurrency}${this.formatter.format(incomeDifference)}` + this.contentService.get('PDF_ANNUITY_INCOME_GAP', ' Income Gap'),
				data: [incomeDifference - 1.4 * this.lineWidth],
				color: PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN,
				grouping: false,
				showInLegend: false
			},
			{
				name:
					`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.desiredIncome))}` + this.contentService.get('YOUR_INCOME_GOAL', ' Your Income Goal'),
				data: [this.lineWidth],
				color: PROGRESS_BAR_COLOR_CONFIG.YOUR_INCOME_GOAL,
				grouping: false
			}
		];
		series.push(...this.getRemainingSeriesData(true));
		return {
			series: series,
			max: Number(this.incomeBarData.expectedIncome),
			goalAchieved: true
		};
	}

	// moving the common series data for annuity into this method
	private getRemainingSeriesData(goalAchieved?: boolean): any {
		const incomeDifference = goalAchieved ? Number(this.incomeBarData.expectedIncome) - Number(this.incomeBarData.desiredIncome) : 0;
		const annuityIncomeLegendLabel = this.userData?.annuityType === EstimatedRetirementIncomeComponent.FIXED_INDEX ? this.contentService.get('PDF_ANNUITY_WITHDRAWAL_FIXED_LEGEND', ' Your Fixed Index Annuity Withdrawal') :
			this.contentService.get('PDF_ANNUITY_WITHDRAWAL_VARIABLE_LEGEND', ' Your Variable Annuity Withdrawal');
		const portfolioIncome = this.isAnnuityVariant ? this.getModelPortfolioIncome() : this.incomeBarData.portfolioIncome;
		return [
			{
				name:
					`${this.displayCurrency}${this.formatter.format(Number(portfolioIncome))}` +
					this.contentService.get('PDF_MODEL_PORTFOLIO_INCOME', ' Model Portfolio Income'),
				data: [Number(portfolioIncome) - incomeDifference],
				color: PROGRESS_BAR_COLOR_CONFIG.WITH_IRETIRE_PLAN,
				grouping: false
			},
			{
				name: `${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.performanceIncome))}`,
				data: [Number(this.incomeBarData.performanceIncome)],
				color: PROGRESS_BAR_COLOR_CONFIG.PERFORMANCE_INCOME,
				grouping: false,
				showInLegend: false
			},
			{
				name: `${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.coreIncome))}${annuityIncomeLegendLabel}`,
				data: [Number(this.incomeBarData.coreIncome)],
				color: PROGRESS_BAR_COLOR_CONFIG.ANNUITY_WITHDRAWAL,
				grouping: false,
				showInLegend: Number(this.incomeBarData.coreIncome) > 0 ? true : false
			},
			{
				name:
					`${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.socialSecurityIncome))}` +
					this.contentService.get('PDF_INPUTS_SOCIAL_SECURITY_INCOME', ' Social Security/Other Income'),
				data: [Number(this.incomeBarData.socialSecurityIncome)],
				color: PROGRESS_BAR_COLOR_CONFIG.SOCIAL_SECURITY_INCOME,
				grouping: false
			},
			{
				name: `${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.expectedIncome))}
					${this.contentService.get('PDF_TOTAL_IRETIRE_PLAN', ' Total with Your iRetire Plan*')}</br>
					${this.contentService.get('PDF_ESTIMATED_INCOME_RANGE_LEGEND', 'Estimated Income Range: ')}
					${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.lowerBoundIncome))}-
					${this.displayCurrency}${this.formatter.format(Number(this.incomeBarData.upperBoundIncome))}
					`,
				data: [0],
				color: PROGRESS_BAR_COLOR_CONFIG.ANNUITY_IRETIRE_PLAN,
				grouping: false
			}
		];
	}

	private getModelPortfolioIncome(): number {
		let portfolioIncome: number;
		const annuityIncome: number = this.incomeBarData?.coreIncome >= 0 && this.incomeBarData?.performanceIncome >= 0 ?
			this.incomeBarData?.performanceIncome + this.incomeBarData.coreIncome : this.incomeBarData?.coreIncome;
		const guaranteedIncome: number = this.incomeBarData?.socialSecurityIncome ? annuityIncome + this.incomeBarData.socialSecurityIncome : annuityIncome;
		portfolioIncome = this.incomeBarData?.expectedIncome - guaranteedIncome;
		return portfolioIncome;
	}
}
